import { State } from "../models/gameModels.ts";
import { User } from "@firebase/auth";
import { doc } from "@firebase/firestore/lite";
import { auth, db, getDoc, setDoc } from "./firebase.ts";
import { getTimer } from "./timer.ts";
import { getCoralogix } from "./coralogix.ts";

export interface GameRecord {
  mistakesRemaining: number;
  success: boolean;
  colorsFound: number[];
  date: number;
  timeTookInSeconds?: number;
}

type FirebaseGameRecord = Omit<GameRecord, "colorsFound"> & {
  colorsFound: Record<string, boolean>;
};

export interface FireBaseHistory {
  history: FirebaseGameRecord[];
}

export const HISTORY_KEY = "userHistory";

export const userHistoryRef = (user: User) => doc(db, "history", user.uid);

const formatFirebaseGameRecords = (gameRecord: GameRecord[]): object => {
  return {
    history: gameRecord.map(({ colorsFound, ...data }) => ({
      ...data,
      colorsFound: Object.fromEntries(
        colorsFound.map((c) => [c.toString(), true]),
      ),
    })),
    puzzlesPlayed: gameRecord.map((r) => r.date),
  };
};

export const getHistoryFromFirebase = async (
  user: User,
): Promise<GameRecord[] | undefined> => {
  const userHistoryDoc = await getDoc(userHistoryRef(user));
  if (!userHistoryDoc.exists()) {
    return undefined;
  }
  return (userHistoryDoc.data() as FireBaseHistory)?.history.map<GameRecord>(
    ({ colorsFound, ...data }) => ({
      ...data,
      colorsFound: Object.keys(colorsFound).map((color) => +color),
    }),
  );
};

export const mergeAndSyncHistory = (
  localHistory: GameRecord[],
  user: User,
  userHistory?: GameRecord[],
): GameRecord[] => {
  let historyToSync: GameRecord[] | undefined;
  if (!userHistory) {
    historyToSync = localHistory;
  } else {
    const userHistoryDates = new Set(userHistory.map((h) => h.date));
    historyToSync = localHistory.filter((h) => !userHistoryDates.has(h.date));
  }
  const history = [...(userHistory ?? []), ...(historyToSync ?? [])];
  if (historyToSync) {
    setDoc(
      userHistoryRef(user),
      formatFirebaseGameRecords([...(userHistory ?? []), ...historyToSync]),
      {},
    ).catch((e) => {
      getCoralogix().error("failed to sync history", e);
    });
  }
  return history;
};

export const readGameHistory = (): GameRecord[] => {
  const lsRecord = localStorage.getItem(HISTORY_KEY);
  if (lsRecord) {
    try {
      const parsed = JSON.parse(lsRecord) as GameRecord[];
      if (Array.isArray(parsed)) {
        return parsed;
      }
      localStorage.removeItem(HISTORY_KEY);
    } catch {
      localStorage.removeItem(HISTORY_KEY);
    }
  }
  return [];
};

export const appendHistory = async (game: State) => {
  const time = getTimer();
  let userHistory = readGameHistory();
  const record: GameRecord = {
    mistakesRemaining: game.mistakesRemaining,
    success: game.result === "win",
    colorsFound: game.complete.map((c) => c.difficulty),
    date: game.dt,
    timeTookInSeconds: time?.seconds,
  };
  if (userHistory.find((r) => r.date === record.date)) {
    return;
  }
  userHistory.push(record);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(userHistory));
  const user = auth.currentUser;
  if (user) {
    const fireBaseHistory = await getHistoryFromFirebase(user);
    mergeAndSyncHistory(userHistory, user, fireBaseHistory);
  }
};
